/**
 * Color math for the theme system.
 *
 * Pure functions, no DOM, no dependencies. That matters for two reasons beyond
 * testability: the contrast gate has to run on the SERVER when a coach saves a
 * theme (client-side only would let an API client store an unreadable theme),
 * and the theme editor needs a live preview without a round trip.
 *
 * The derivation works in OKLCH and emits sRGB, per BUILD_PLAN 9.3. Emitting
 * OKLCH directly would tie the rendered palette to browser color management in
 * ways that make the gate's arithmetic and the actual pixels disagree, which is
 * worse than having no gate.
 */

export interface Rgb {
  readonly r: number;
  readonly g: number;
  readonly b: number;
}

export interface Oklch {
  readonly l: number;
  readonly c: number;
  readonly h: number;
}

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

export function parseHex(hex: string): Rgb | null {
  const cleaned = hex.trim().replace(/^#/, '');
  const expanded =
    cleaned.length === 3
      ? cleaned
          .split('')
          .map((c) => c + c)
          .join('')
      : cleaned;
  if (!/^[0-9a-fA-F]{6}$/.test(expanded)) return null;
  return {
    r: parseInt(expanded.slice(0, 2), 16),
    g: parseInt(expanded.slice(2, 4), 16),
    b: parseInt(expanded.slice(4, 6), 16),
  };
}

export function toHex({ r, g, b }: Rgb): string {
  const part = (v: number) =>
    Math.round(clamp(v, 0, 255))
      .toString(16)
      .padStart(2, '0');
  return `#${part(r)}${part(g)}${part(b)}`;
}

/** sRGB 0..255 to linear 0..1. */
function toLinear(channel: number): number {
  const v = channel / 255;
  return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
}

function fromLinear(value: number): number {
  const v = value <= 0.0031308 ? value * 12.92 : 1.055 * Math.pow(value, 1 / 2.4) - 0.055;
  return clamp(v * 255, 0, 255);
}

/**
 * WCAG relative luminance.
 *
 * The formula from WCAG 2.1, not a perceptual approximation. The gate has to
 * agree with whatever an auditor's tool reports, and the axe scan in the
 * machine gates uses this definition.
 */
export function relativeLuminance(color: Rgb): number {
  return 0.2126 * toLinear(color.r) + 0.7152 * toLinear(color.g) + 0.0722 * toLinear(color.b);
}

/** WCAG contrast ratio, 1 to 21. Order of arguments does not matter. */
export function contrastRatio(a: Rgb, b: Rgb): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const lighter = Math.max(la, lb);
  const darker = Math.min(la, lb);
  return (lighter + 0.05) / (darker + 0.05);
}

export function rgbToOklch(color: Rgb): Oklch {
  const r = toLinear(color.r);
  const g = toLinear(color.g);
  const b = toLinear(color.b);

  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);

  const okL = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
  const okA = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const okB = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;

  const chroma = Math.sqrt(okA * okA + okB * okB);
  const hue = ((Math.atan2(okB, okA) * 180) / Math.PI + 360) % 360;
  return { l: okL, c: chroma, h: hue };
}

/** Linear-light sRGB, unclamped, so gamut membership can be tested. */
function oklchToLinear({ l, c, h }: Oklch): { r: number; g: number; b: number } {
  const hRad = (h * Math.PI) / 180;
  const okA = c * Math.cos(hRad);
  const okB = c * Math.sin(hRad);

  const lc = (l + 0.3963377774 * okA + 0.2158037573 * okB) ** 3;
  const mc = (l - 0.1055613458 * okA - 0.0638541728 * okB) ** 3;
  const sc = (l - 0.0894841775 * okA - 1.291485548 * okB) ** 3;

  return {
    r: 4.0767416621 * lc - 3.3077115913 * mc + 0.2309699292 * sc,
    g: -1.2684380046 * lc + 2.6097574011 * mc - 0.3413193965 * sc,
    b: -0.0041960863 * lc - 0.7034186147 * mc + 1.707614701 * sc,
  };
}

/** Converts with per-channel clamping. Fast, and wrong at the gamut edges. */
export function oklchToRgb(color: Oklch): Rgb {
  const linear = oklchToLinear(color);
  return {
    r: fromLinear(linear.r),
    g: fromLinear(linear.g),
    b: fromLinear(linear.b),
  };
}

const EPSILON = 1e-6;

function isInGamut(linear: { r: number; g: number; b: number }): boolean {
  return (
    linear.r >= -EPSILON &&
    linear.r <= 1 + EPSILON &&
    linear.g >= -EPSILON &&
    linear.g <= 1 + EPSILON &&
    linear.b >= -EPSILON &&
    linear.b <= 1 + EPSILON
  );
}

/**
 * Convert to sRGB, reducing chroma until the color fits in the gamut.
 *
 * Naive per-channel clamping is what a palette generator reaches for first, and
 * it shifts HUE at the light and dark ends of a ramp, because the three
 * channels clip by different amounts. A blue seed produces a step-50 tint that
 * measures 50 degrees away in hue: visibly the wrong color at the top of the
 * ramp, on a screen where that tint is a card background.
 *
 * Binary searching chroma instead preserves hue and lightness exactly and gives
 * up only saturation, which is the one of the three nobody notices at 97
 * percent lightness.
 */
export function oklchToRgbInGamut({ l, c, h }: Oklch): Rgb {
  if (isInGamut(oklchToLinear({ l, c, h }))) {
    return oklchToRgb({ l, c, h });
  }

  let low = 0;
  let high = c;
  for (let i = 0; i < 24; i += 1) {
    const mid = (low + high) / 2;
    if (isInGamut(oklchToLinear({ l, c: mid, h }))) {
      low = mid;
    } else {
      high = mid;
    }
  }
  return oklchToRgb({ l, c: low, h });
}
